/* eslint-disable max-classes-per-file */
// @ts-strict
import {
    DocumentType,
    modelOptions,
    mongoose,
    PropType,
    plugin,
    post,
    pre,
    prop,
    type Ref,
} from "@typegoose/typegoose";
import mongooseAutoPopulate from "mongoose-autopopulate";
import { z } from "zod";
import { AssetType } from "../../Configurations/AssetTypeConfiguration/AssetTypeConfiguration.js";
import { RootLogger } from "../../logging/index.js";
import { PinoModules } from "../../utils/index.js";
import { getZodSchemaFromClass, ObjectIdString, validate } from "../../zod-decorator/index.js";
import { AreaOfInterest } from "../areaOfInterest/areaOfInterest.js";
import { assetIdentitySync } from "../assetIdentity/assetIdentitySyncHook.js";
import { VertexInfo } from "../commonSubSchemas/vertexInfo.js";
import type { Organization } from "../organization/organization.js";
import { ResponseTimeStats } from "../responseTimeStats/responseTimeStats.js";
import { addSchemaKeyHook, getDefaultSchemaOptions, SchemaBase, SchemaConstructorArgs } from "../schemaBase.js";
import { ZoneFieldsWithDefaults, ZoneSchemaTypes } from "../types/zone.d.js";
import { createDelayedRegistrationHook } from "../utils/delayedRegistrationHook.js";
import type { DtoValidatorTypes } from "../validatableClass.js";
import { CurrentIntendedState } from "./subSchemas/currentIntendedState.js";
import { SoilLayer } from "./subSchemas/soilLayer.js";
import { SupplementalZoneGraphInfo } from "./subSchemas/supplementalZoneGraphInfo.js";
import { ZoneMetadata } from "./subSchemas/zoneMetadata.js";
import * as utilities from "./utilities.js";

const logger = RootLogger.child({ module: PinoModules.models.assetIdentity });

const SCHEMA_KEY = "Zone" as const;
/** MongoDB collection name for {@link Zone} documents. */
export const ZONE_COLLECTION_NAME = "zones" as const;

/**
 * Generates and assigns a unique verdiId to the zone. Registered in the backend
 * (where AssetIdentityModel lives) because verdi-common cannot import backend code.
 */
export const zoneVerdiIdGenerationPreSave =
    createDelayedRegistrationHook<(document: DocumentType<Zone>) => Promise<void>>("zoneVerdiIdGenerationPreSave");

@pre<Zone>("save", async function (this: DocumentType<Zone>) {
    if (this.verdiId) return;
    try {
        await zoneVerdiIdGenerationPreSave.function(this);
    } catch (e) {
        logger.warn({ err: e, zoneId: this._id }, "could not generate verdiId for zone");
    }
})
@post<Zone>("save", async function (this: DocumentType<Zone>) {
    if (!this.verdiId) return;
    // TODO: Once AssetIdentity becomes the source of truth for asset IDs, this should hard-fail instead of
    // catching. We swallow errors here only because the transition is incomplete — zone saves must not
    // break if the sync fails during this interim period.
    await assetIdentitySync
        .function({
            assetCollection: ZONE_COLLECTION_NAME,
            assetType: AssetType.ZONE,
            verdiId: this.verdiId,
        })
        .catch((e) => logger.warn({ err: e, verdiId: this.verdiId }, "could not sync asset identity for zone"));
})
@modelOptions({
    schemaOptions: {
        ...getDefaultSchemaOptions(SCHEMA_KEY),
        _id: true,
        collection: ZONE_COLLECTION_NAME,
        timestamps: true,
    },
})
@plugin(mongooseAutoPopulate as any)
@post<SchemaBase<Zone>>(...addSchemaKeyHook(SCHEMA_KEY))
export class Zone extends SchemaBase<Zone, ZoneFieldsWithDefaults> {
    static readonly schemaKey = SCHEMA_KEY;

    @validate({ schemaKey: SCHEMA_KEY })
    readonly schemaKey = SCHEMA_KEY;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    public _id!: mongoose.Types.ObjectId;

    @validate({ zodValidator: z.string().min(1) })
    @prop({ type: () => String, required: true })
    public name!: string;

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: () => "" })
    public notes: string = "";

    // if required is true, empty strings are rejected. This is stupid, but I don't have a better solution to deal with it right now
    // other than to make it optional in typegoose, but have a default (meaning it will functionally always be populated)
    @validate({ zodValidator: z.string().optional() })
    @prop({ type: () => String, required: false, default: () => "" })
    public adminNotes: string = "";

    @validate({
        childClass: VertexInfo,
        childClassTransform: (object) => z.array(z.array(object)),
    })
    @prop({ type: () => [[VertexInfo]], required: true }, PropType.ARRAY)
    public polygons!: VertexInfo[][];

    @validate({
        childClass: CurrentIntendedState,
        childClassTransform: (object) => object.optional(),
        responseOnly: true,
    })
    @prop({ type: () => CurrentIntendedState, required: false })
    public currentIntendedState?: CurrentIntendedState;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastVerification?: Date;

    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: true, default: new Date() })
    public created: Date = new Date();

    @validate({
        childClass: SoilLayer,
        childClassTransform: (object) => z.array(object).optional(),
    })
    @prop({ type: () => SoilLayer, required: false }, PropType.ARRAY)
    public soilLayers?: SoilLayer[];

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: false })
    public soilCapacity?: number;

    @validate({ zodValidator: z.boolean(), responseOnly: true })
    @prop({ type: () => Boolean, required: true, default: false })
    public deleted: boolean = false;

    @validate({ zodValidator: ObjectIdString, responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: true, ref: "Organization" })
    public owner!: Ref<Organization>;

    @validate({
        childClass: ZoneMetadata,
        childClassTransform: (object) => object.optional(),
        allowPartialOnUpdate: false,
    })
    @prop({ type: () => ZoneMetadata, required: false })
    public userMetadata?: ZoneMetadata;

    @validate({
        childClass: SupplementalZoneGraphInfo,
        childClassTransform: (object) => object.optional(),
    })
    @prop({ type: () => SupplementalZoneGraphInfo, required: false })
    public supplementalZoneGraphInfo?: SupplementalZoneGraphInfo;

    @validate({ zodValidator: ObjectIdString.optional(), responseOnly: true })
    @prop({ type: () => mongoose.Types.ObjectId, required: false, ref: "AreaOfInterest" })
    public _aoi?: Ref<AreaOfInterest>;

    // will become required once it gets fully propagated.
    // sparse so zones without a verdiId aren't treated as duplicates of one another.
    @validate({ zodValidator: z.string().optional(), responseOnly: true })
    @prop({ type: () => String, required: false, unique: true, sparse: true })
    public verdiId?: string;

    @validate({
        childClass: ResponseTimeStats,
        // autopopulate returns null (not undefined) when no matching ResponseTimeStats doc exists
        childClassTransform: (object) => object.nullish(),
        responseOnly: true,
    })
    @prop({
        ref: () => ResponseTimeStats,
        localField: "_id",
        foreignField: "foreignLookup.targetId",
        match: { "foreignLookup.targetCollection": ZONE_COLLECTION_NAME },
        justOne: true,
        virtual: true,
        autopopulate: true,
    })
    public responseTimeStats: ResponseTimeStats | null | undefined = undefined;

    // For updating zone histories
    /**
     * Defines the last time the zone was surveyed and therefore the time period that needs to be scanned
     * during the next survey. Used in ManageZoneHistory
     */
    @validate({ zodValidator: z.coerce.date().optional(), responseOnly: true })
    @prop({ type: () => Date, required: false })
    public lastSurveyed?: Date;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: true, default: 1.0000001 })
    public defaultRelativeIrrigationDuration?: number = 1.0000001;

    @validate({ zodValidator: z.number().optional() })
    @prop({ type: () => Number, required: true, default: 1.0000001 })
    public recommendedRelativeIrrigationDuration?: number = 1.0000001;

    static utilities = { ...utilities } as const;

    static dtoValidators: DtoValidatorTypes<ZoneSchemaTypes>;

    constructor(properties?: SchemaConstructorArgs<Zone, ZoneFieldsWithDefaults>) {
        super(properties);
        Object.assign(this, properties);
    }
}

Zone.dtoValidators = getZodSchemaFromClass(Zone) as unknown as DtoValidatorTypes<ZoneSchemaTypes>;
